<template>
  <bm-dialog
    ref="bmDialogRef"
    :label="messages.crm_log_communication"
    :width="1000"
    @cancel="cancel"
    @accept="validateAndAccept"
  >
    <communication-log-control
      :value.sync="communicationLogEntry"
      :cache="cache"
    >
      <template v-if="entityType?.isOutlet()" #prepend>
        <v-row>
          <v-col cols="12">
            <bm-dropdown
              :value.sync="communicationLogEntry.entityId"
              :t="keys.outlet"
              :items="cache.outlets.all"
              :item-text="(item) => [item.displayName, item.code]"
              :item-value="(item) => item.id"
              :rules="[$ruleSet.required()]"
            />
          </v-col>
        </v-row>
      </template>

      <template v-if="showCompleteTaskCheckbox" #append>
        <v-row>
          <v-col cols="12">
            <bm-checkbox
              :value.sync="completeTask"
              :t="keys.tasks_complete_task"
            />
          </v-col>
        </v-row>
      </template>
    </communication-log-control>

    <template #controls-confirm="{ isDesktopView }">
      <bm-btn
        :t="keys.save"
        :block="isDesktopView"
        :loading="saving"
        @click="validateAndAccept"
      />
    </template>
  </bm-dialog>
</template>

<script setup lang="ts">
import BmBtn from "@/components/controls/input/BmBtn.vue";
import BmDialog from "@/components/dialogs/BmDialog.vue";
import type { BmTask } from "@/app/services/generated/bmtaskService";
import { onMounted, ref, watchEffect } from "vue";
import {
  addCommunicationLogEntry,
  CommunicationLogEntry,
  createCommunicationLogEntryWithDefaults,
} from "@/components/pages/outlet/crm/communication_logs/communicationLog";
import type { BmFormInterface } from "@/components/controls/BmFormTypes";
import {
  useReactiveDialogScriptSetup,
  type DialogEvents,
} from "@/components/mixins/dialogs";
import { EnumBmEntityType } from "@/app/services/generated/enums/enumBmEntityType.generated";
import type Guid from "@/app/types/common/guid";
import CommunicationLogControl from "@/components/pages/outlet/crm/communication_logs/CommunicationLogControl.vue";
import BmDropdown from "@/components/controls/input/BmDropdown.vue";
import apiCache from "@/app/caching/apiCache";
import OutletSummariesCache from "@/app/caching/caches/outletSummariesCache";
import messages, { keys } from "@/plugins/i18n";
import useLoadingState from "@/app/useLoadingState";
import { makeDeepRef } from "@/components/composables/reactivityTypes";
import CommunicationTypesCache from "@/app/caching/caches/communicationTypesCache";
import CommunicationSubjectsCache from "@/app/caching/caches/communicationSubjectsCache";

export interface LogCommunicationDialogPayload {
  completeTask: boolean;
}

interface LogCommunicationDialogProps {
  task?: BmTask;
  showCompleteTask?: boolean;
  entityType?: EnumBmEntityType;
  entityId?: Guid;
}

interface LogCommunicationDialogEvents extends DialogEvents<LogCommunicationDialogPayload> {}

const props = withDefaults(defineProps<LogCommunicationDialogProps>(), {
  entityType: () => EnumBmEntityType.outlet,
});

const emit = defineEmits<LogCommunicationDialogEvents>();

const bmDialogRef = ref<BmFormInterface | null>(null);
const completeTask = ref(true);
const showCompleteTaskCheckbox = ref(true);

const communicationLogEntry = makeDeepRef(
  new CommunicationLogEntry(
    props.entityId ?? props.task?.associatedBmEntityId ?? null
  )
);

const cache = apiCache.buildCacheSet({
  outlets: OutletSummariesCache,
  communicationTypes: CommunicationTypesCache,
  communicationSubjects: CommunicationSubjectsCache,
});

const { accept, cancel } =
  useReactiveDialogScriptSetup<LogCommunicationDialogPayload>(emit);

const [saving, validateAndAccept] = useLoadingState(async () => {
  if (!bmDialogRef.value?.validate()) {
    return;
  }

  await addCommunicationLogEntry({
    communicationLogEntry: communicationLogEntry.value,
    entityType: props.entityType,
  });

  const payload: LogCommunicationDialogPayload = {
    completeTask: completeTask.value,
  };

  accept(payload);
});

onMounted(async () => {
  await cache.update();

  communicationLogEntry.value = createCommunicationLogEntryWithDefaults({
    communicationSubjects: cache.communicationSubjects.all,
    communicationTypes: cache.communicationTypes.all,
    entityId: props.entityId ?? props.task?.associatedBmEntityId ?? null,
  });
});

watchEffect(() => {
  // if dropdown value changes from original value, hide checkbox and
  // don't allow task competion
  if (
    !props.showCompleteTask ||
    (props.task?.associatedBmEntityId &&
      communicationLogEntry.value.entityId &&
      !communicationLogEntry.value.entityId.equals(
        props.task.associatedBmEntityId
      ))
  ) {
    completeTask.value = false;
    showCompleteTaskCheckbox.value = false;
  } else {
    showCompleteTaskCheckbox.value = true;
  }
});
</script>
